import { computed, ref } from "vue";
import { useStore } from "@/core/services/store";
import i18n from "@/core/plugins/vue-i18n";
import { Confirmation, Error } from "@/core/plugins/swal";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import { useFeatures } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatures";
import { useFeatureArea } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatureArea";
import SalesChannelService, {
  isLegacyInvoiceServiceActive
} from "@/components/ExternalApps/SalesChannelManagementApp/services/salesChannel";

const store = useStore();
const { formValueChange } = useIntegration();
const { selectedFeature, features, featuresWithChangedActiveState } =
  useFeatures();
const { disableDependingOnFeatureStatus } = useFeatureArea();
const formHelper = ref([]);
const showReceiptActivationDialog = ref(false);
// idle | loading | active | inactive | error — state of the proactive legacy
// sales channel check backing the receipt activation modal
const receiptChannelCheck = ref("idle");
let pendingReceiptActivation = null;

export const useAppHeader = () => {
  // Live-/Devmode in features area
  const featureBaseForm = computed(() => {
    if (selectedFeature.value === null) {
      return [];
    }

    return store.getters.form("app." + selectedFeature.value + ".baseSettings");
  });

  const activeIntegration = computed(() => {
    return store.getters.selectedIntegration;
  });

  /**
   * Function to trigger confirmation when changing the status of an integration
   * @param baseSetting
   * @param event
   * @param selectedFeature
   * @param area
   */
  function triggerConfirmation(baseSetting, event, selectedFeature, area) {
    // Only the active field switches live/dev mode and needs confirmation.
    // Other baseSettings fields (e.g. selects) save without the modal.
    if (event.name !== "active") {
      formValueChange(event, selectedFeature, area, features.value);
      return;
    }

    // Amazon receipt activation gets a dedicated modal (CONNECT-6209) warning
    // about the legacy shopimporter invoice feature. All other cases keep the
    // generic live/dev-mode confirmation.
    const isAmazonReceiptActivation =
      store.getters.selectedApp?.name === "amazon" &&
      selectedFeature === "receipt" &&
      event.value === true;

    if (isAmazonReceiptActivation) {
      pendingReceiptActivation = { baseSetting, event, selectedFeature, area };
      showReceiptActivationDialog.value = true;
      checkLegacySalesChannel();
      return;
    }

    let params = {
      title: i18n.t("salesChannelManagementApp.changeActiveStatusTitle"),
      text: baseSetting.value.active
        ? i18n.t("salesChannelManagementApp.changeActiveStatusTextInactive")
        : i18n.t("salesChannelManagementApp.changeActiveStatusTextActive"),
      cancelButtonText: i18n.t("general.cancel"),
      confirmButtonText: i18n.t("general.confirm")
    };

    Confirmation(params).then(result => {
      if (!result.isConfirmed) {
        return revertActiveChange(baseSetting);
      }
      applyActiveChange(baseSetting, event, selectedFeature, area);
    });
  }

  /**
   * Applies a confirmed live/dev-mode change for a feature.
   * @param baseSetting
   * @param event
   * @param selectedFeature
   * @param area
   */
  function applyActiveChange(baseSetting, event, selectedFeature, area) {
    // Validate mandatory fields if feature changes to live mode
    if (event.value) {
      if (!validateForms()) {
        Error(i18n.t("salesChannelManagementApp.livemodeError"));
        return (baseSetting.value.active = false);
      }
    }

    disableDependingOnFeatureStatus();

    formValueChange(event, selectedFeature, area, features.value);

    featuresWithChangedActiveState.value[selectedFeature] = event.value;
  }

  /**
   * Reverts the toggle when a live/dev-mode change is cancelled.
   * @param baseSetting
   */
  function revertActiveChange(baseSetting) {
    baseSetting.value.active = !baseSetting.value.active;
  }

  /**
   * Proactively checks whether the linked legacy Amazon shopimporter still
   * has its invoice service enabled. Runs when the receipt activation modal
   * opens; the modal disables its confirm button unless the result is
   * "inactive". Fails closed: an unverifiable channel state blocks
   * activation.
   */
  async function checkLegacySalesChannel() {
    const channelUuid =
      store.getters.selectedIntegration?.salesChannel?.value?.uuid;

    // No linked legacy sales channel (e.g. non-migrated integration) —
    // nothing to block on.
    if (!channelUuid) {
      receiptChannelCheck.value = "inactive";
      return;
    }

    receiptChannelCheck.value = "loading";

    try {
      // ApiService returns the parsed body directly (flat channel config)
      const salesChannel = await SalesChannelService.get(channelUuid);

      receiptChannelCheck.value = isLegacyInvoiceServiceActive(salesChannel)
        ? "active"
        : "inactive";
    } catch (e) {
      receiptChannelCheck.value = "error";
    }
  }

  /**
   * Confirm handler for the Amazon receipt activation modal. Only applies the
   * change when the legacy channel check passed (the modal disables confirm
   * otherwise).
   */
  function confirmReceiptActivation() {
    if (receiptChannelCheck.value !== "inactive") return;

    const pending = pendingReceiptActivation;
    pendingReceiptActivation = null;
    showReceiptActivationDialog.value = false;

    if (!pending) return;

    applyActiveChange(
      pending.baseSetting,
      pending.event,
      pending.selectedFeature,
      pending.area
    );
  }

  /**
   * Cancel handler for the Amazon receipt activation modal.
   */
  function cancelReceiptActivation() {
    showReceiptActivationDialog.value = false;

    const pending = pendingReceiptActivation;
    pendingReceiptActivation = null;

    if (pending) revertActiveChange(pending.baseSetting);
  }

  function validateForms() {
    if (!formHelper.value) return true;

    let counter = 0;
    for (const form of formHelper.value) {
      if (
        !form ||
        !("validate" in form) ||
        typeof form.validate !== "function"
      ) {
        continue;
      }
      if (form?.validate() !== true) {
        counter++;
      }
    }
    return counter === 0;
  }

  return {
    featureBaseForm,
    activeIntegration,
    triggerConfirmation,
    formHelper,
    validateForms,
    showReceiptActivationDialog,
    receiptChannelCheck,
    confirmReceiptActivation,
    cancelReceiptActivation
  };
};
